import { useQuery } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, Download, RefreshCw } from "lucide-react";
import {
  useCanAccess,
  useGetIdentity,
  useGetList,
  useNotify,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { FunnelShell } from "../funnel/shared";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";
import { buildSystemReport } from "./report";

type LogRow = {
  id: number;
  at: string;
  level: "error" | "warn" | "info";
  source: string;
  message: string;
  details: Record<string, unknown>;
  sales_id: number | null;
};
type EventRow = {
  id: number;
  occurred_at: string;
  type: string;
  sales_id: number | null;
  deal_id: number | null;
  payload: Record<string, unknown>;
};
type Health = {
  errors_24h: number;
  last_lead_at: string | null;
  leads_today: number;
  telegram_queue: number;
  telegram_sent_24h: number;
  users: number;
  cron: {
    job: string;
    schedule: string;
    last_status: string | null;
    last_run: string | null;
    last_error: string | null;
  }[];
};

/** Developer's page: system health, errors and everything people did */
export const SystemPage = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "system",
    action: "list",
  });
  if (isPending) return null;
  if (!canAccess) return <Navigate to="/" />;
  return <SystemLog />;
};
SystemPage.path = "/system";

const ago = (iso: string | null, locale: string) => {
  if (!iso) return "—";
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (minutes < 60) return rtf.format(-minutes, "minute");
  if (minutes < 48 * 60) return rtf.format(-Math.round(minutes / 60), "hour");
  return rtf.format(-Math.round(minutes / 1440), "day");
};

const SystemLog = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [tab, setTab] = useState<"errors" | "actions">("errors");
  const [level, setLevel] = useState<"" | "error" | "warn">("");
  const [open, setOpen] = useState<number>();
  const [building, setBuilding] = useState(false);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const download = async () => {
    setBuilding(true);
    try {
      await buildSystemReport(identity?.fullName ?? "");
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setBuilding(false);
    }
  };
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`crm.system.${key}`, args);

  const health = useQuery({
    queryKey: ["system_health"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("system_health");
      if (error) throw error;
      return data as Health;
    },
    refetchInterval: 30_000,
  });
  const logs = useGetList<LogRow>("system_log", {
    filter: level ? { level } : {},
    pagination: { page: 1, perPage: 200 },
    sort: { field: "at", order: "DESC" },
  });
  const events = useGetList<EventRow>(
    "events",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "occurred_at", order: "DESC" },
    },
    { enabled: tab === "actions" },
  );
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const who = (id: number | null) => {
    const sale = sales.find((s) => s.id === id);
    return sale ? `${sale.first_name} ${sale.last_name}` : t("system_actor");
  };
  const time = (iso: string) =>
    new Date(iso).toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  const h = health.data;
  const quietHours = h?.last_lead_at
    ? (Date.now() - Date.parse(h.last_lead_at)) / 3_600_000
    : null;

  const cards = [
    {
      key: "errors",
      value: h?.errors_24h ?? "—",
      sub: t("errors_sub"),
      bad: (h?.errors_24h ?? 0) > 0,
    },
    {
      key: "last_lead",
      value: ago(h?.last_lead_at ?? null, locale),
      sub: t("leads_today", { count: h?.leads_today ?? 0 }),
      bad: quietHours != null && quietHours > 12,
    },
    {
      key: "telegram",
      value: h?.telegram_sent_24h ?? "—",
      sub: t("telegram_sub", { count: h?.telegram_queue ?? 0 }),
      bad: (h?.telegram_queue ?? 0) > 5,
    },
    {
      key: "users",
      value: h?.users ?? "—",
      sub: t("users_sub"),
      bad: false,
    },
  ];

  return (
    <FunnelShell
      title={t("title")}
      actions={
        <div className="ml-auto flex gap-2">
          <Button type="button" onClick={download} disabled={building}>
            <Download className="mr-1 size-4" />
            {t(building ? "report_building" : "report")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              health.refetch();
              logs.refetch();
              events.refetch();
            }}
          >
            <RefreshCw className="mr-1 size-4" />
            {t("refresh")}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.key}
            className={cn(
              "rounded-2xl border bg-card p-4",
              card.bad && "border-destructive/40 bg-destructive/5",
            )}
          >
            <div className="text-sm text-muted-foreground">
              {t(`cards.${card.key}`)}
            </div>
            <div
              className={cn(
                "text-2xl font-extrabold tabular-nums sm:text-3xl",
                card.bad && "text-destructive",
              )}
            >
              {card.value}
            </div>
            <div className="text-xs text-muted-foreground">{card.sub}</div>
          </div>
        ))}
      </div>

      <section className="rounded-2xl border bg-card p-5">
        <h2 className="mb-3 text-lg font-bold">{t("jobs")}</h2>
        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {h?.cron.map((job) => {
            const ok = job.last_status !== "failed";
            return (
              <li
                key={job.job}
                className="flex items-start gap-2 rounded-xl border p-3 text-sm"
              >
                {ok ? (
                  <CircleCheck className="mt-0.5 size-4 shrink-0 text-[#17703c]" />
                ) : (
                  <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
                )}
                <div className="min-w-0">
                  <div className="font-mono text-xs font-semibold">
                    {job.job}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {job.schedule} · {ago(job.last_run, locale)}
                  </div>
                  {!ok && job.last_error ? (
                    <div className="mt-1 truncate text-xs text-destructive">
                      {job.last_error}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border bg-card p-1 text-sm">
          {(["errors", "actions"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={cn(
                "rounded-lg px-4 py-1.5 font-semibold",
                tab === value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground",
              )}
            >
              {t(`tabs.${value}`)}
            </button>
          ))}
        </div>
        {tab === "errors" ? (
          <div className="inline-flex rounded-xl border bg-card p-1 text-sm">
            {(["", "error", "warn"] as const).map((value) => (
              <button
                key={value || "all"}
                type="button"
                onClick={() => setLevel(value)}
                className={cn(
                  "rounded-lg px-3 py-1.5 font-semibold",
                  level === value
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {t(`levels.${value || "all"}`)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {tab === "errors" ? (
        <section className="overflow-hidden rounded-2xl border bg-card">
          {logs.data?.length ? (
            <ul className="divide-y font-mono text-xs">
              {logs.data.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setOpen(open === row.id ? undefined : row.id)
                    }
                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-muted/50"
                  >
                    <span
                      className={cn(
                        "shrink-0 rounded px-1.5 py-0.5 font-bold uppercase",
                        row.level === "error"
                          ? "bg-destructive/15 text-destructive"
                          : row.level === "warn"
                            ? "bg-[#fbefd2] text-[#7a4f00]"
                            : "bg-muted text-muted-foreground",
                      )}
                    >
                      {row.level}
                    </span>
                    <span className="shrink-0 text-muted-foreground">
                      {time(row.at)}
                    </span>
                    <span className="shrink-0 font-semibold">{row.source}</span>
                    <span className="min-w-0 flex-1 break-words">
                      {row.message}
                    </span>
                  </button>
                  {open === row.id ? (
                    <pre className="overflow-x-auto bg-muted/40 px-4 py-3 text-[11px] whitespace-pre-wrap">
                      {row.sales_id ? `${who(row.sales_id)}\n` : ""}
                      {JSON.stringify(row.details, null, 2)}
                    </pre>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex items-center gap-2 p-5 text-sm text-[#17703c]">
              <CircleCheck className="size-4" />
              {t("no_errors")}
            </p>
          )}
        </section>
      ) : (
        <section className="overflow-x-auto rounded-2xl border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2">{t("col.time")}</th>
                <th className="px-4 py-2">{t("col.who")}</th>
                <th className="px-4 py-2">{t("col.what")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {events.data?.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-4 py-2 whitespace-nowrap text-muted-foreground tabular-nums">
                    {time(row.occurred_at)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {who(row.sales_id)}
                  </td>
                  <td className="px-4 py-2">
                    {translate(
                      `crm.system.events.${row.type.replace(/\./g, "_")}`,
                      {
                        _: row.type,
                        ...Object.fromEntries(
                          Object.entries(row.payload ?? {}).map(([k, v]) => [
                            k,
                            String(v),
                          ]),
                        ),
                      },
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {row.deal_id ? (
                      <Link
                        to={`/funnel/${row.deal_id}`}
                        className="text-xs font-semibold text-primary underline"
                      >
                        {t("open")}
                      </Link>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </FunnelShell>
  );
};
